import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, CreditCard, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Btn } from "@/components/account/Shell";

/** The one-time "you're live" banner (7.12): the shop link and a QR code. Shows once, after Go live. */
export function LiveBanner() {
    const [params, setParams] = useSearchParams();
    const [card, setCard] = useState(null);
    const live = params.get("live") === "1";
    useEffect(() => {
        if (live) api.get("/signup/live-card", { params: { origin: window.location.origin } }).then(({ data }) => setCard(data)).catch(() => {});
    }, [live]);
    if (!live || !card) return null;
    const close = () => { params.delete("live"); setParams(params, { replace: true }); };
    const copy = () => { navigator.clipboard.writeText(card.url); toast.success("Link copied"); };
    return (
        <div className="flex flex-wrap items-center gap-4 border-2 border-[#1a1a1a] bg-white p-4" data-testid="live-banner">
            <img src={card.qr} alt="QR code for your shop" className="h-28 w-28 border border-[#d0d0d0]" data-testid="live-banner-qr" />
            <div className="min-w-[220px] flex-1">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#1e6b34]">Your shop is live</p>
                <p className="mt-1 text-[15px] font-semibold text-[#1a1a1a]">{card.name}</p>
                <a href={card.url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-[14px] text-[#1a56db] underline" data-testid="live-banner-url">{card.url}</a>
                <p className="mt-1 text-[12px] text-[#4a4a4a]">Also at {card.subdomain_url}{card.custom_domain ? ` and ${card.custom_domain} once its DNS points here` : ""}.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <Btn onClick={copy} testid="live-banner-copy"><Copy size={12} /> Copy link</Btn>
                    <a href={card.url} target="_blank" rel="noreferrer"><Btn tone="solid" testid="live-banner-open">Open my shop</Btn></a>
                </div>
            </div>
            <button type="button" onClick={close} aria-label="Dismiss" data-testid="live-banner-close" className="self-start p-1 text-[#4a4a4a] hover:text-[#1a1a1a]"><X size={16} /></button>
        </div>
    );
}

/** The plan is billed after go-live: $199 the first month, then $99 a month. Hidden once paid or comped. */
export function PlanBar() {
    const [params] = useSearchParams();
    const [b, setB] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/signup/billing").then(({ data }) => setB(data)).catch(() => setB(false));
    useEffect(() => {
        const sid = params.get("session_id");
        if (params.get("plan") === "paid" && sid) {
            api.get(`/payments/status/${sid}`).then(() => { toast.success("Plan started — thank you."); load(); }).catch(load);
        } else load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    if (!b || b.paid) return null;
    const start = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key: b.lookup_key, origin_url: window.location.origin,
                success_path: "/account?plan=paid&session_id={CHECKOUT_SESSION_ID}", cancel_path: "/account",
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not open Stripe");
            setBusy(false);
        }
    };
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-[#1a1a1a] bg-[#fafafa] px-3 py-2.5" data-testid="plan-bar">
            <CreditCard size={16} className="shrink-0" />
            <p className="min-w-[220px] flex-1 text-[13px] text-[#1a1a1a]">
                <span className="font-semibold">Start your {b.plan} plan.</span>{" "}
                ${b.first_month} the first month, then ${b.price_month} a month for as long as you stay. Your shop is already live.
            </p>
            <Btn tone="solid" onClick={start} disabled={busy} testid="plan-bar-start">
                {busy ? <Loader2 size={12} className="animate-spin" /> : null} Start my plan — ${b.first_month}
            </Btn>
        </div>
    );
}
